import React, { useState, useEffect } from 'react';
import EditableBlock, { EditableBlockFooter } from './EditableBlock';
import { authHeaderOrEmpty } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Segmentation: Origine (read-only) + Groupes officiels + Tags personnels
 *
 * Différence clé (cf. mockup v4) :
 *   - Tags = étiquettes libres, personnelles (ne déclenchent rien)
 *   - Groupes = segments officiels (utilisés pour les campagnes marketing)
 */
const SegmentationBlock = ({ contact, onSave }) => {
  const [editGroupes, setEditGroupes] = useState(contact?.groupes || []);
  const [editTags, setEditTags] = useState(contact?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [availableGroupes, setAvailableGroupes] = useState([]);
  const [showGroupePicker, setShowGroupePicker] = useState(false);

  useEffect(() => {
    setEditGroupes(contact?.groupes || []);
    setEditTags(contact?.tags || []);
  }, [contact?.id, contact?.groupes, contact?.tags]);

  // Charger la liste des groupes officiels disponibles depuis le backend
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/contacts/types/groupes`, {
          headers: authHeaderOrEmpty(),
        });
        if (res.ok) {
          const data = await res.json();
          // L'endpoint peut renvoyer un tableau ou un objet { groupes: [...] }
          const list = Array.isArray(data) ? data : data?.groupes || [];
          setAvailableGroupes(list);
        }
      } catch (err) {
        console.warn('[Segmentation] Impossible de charger les groupes:', err);
      }
    })();
  }, []);

  const handleAddTag = () => {
    const tag = (newTag || '').trim();
    if (!tag) return;
    if (editTags.includes(tag)) return;
    setEditTags([...editTags, tag]);
    setNewTag('');
  };

  const removeTag = (tag) => setEditTags(editTags.filter((t) => t !== tag));
  const removeGroupe = (g) => setEditGroupes(editGroupes.filter((x) => x !== g));

  const addGroupe = (g) => {
    if (!editGroupes.includes(g)) {
      setEditGroupes([...editGroupes, g]);
    }
    setShowGroupePicker(false);
  };

  const promoteTagToGroup = (tag) => {
    // Ajoute aux groupes et retire des tags
    setEditTags(editTags.filter((t) => t !== tag));
    if (!editGroupes.includes(tag)) {
      setEditGroupes([...editGroupes, tag]);
    }
  };

  const handleSave = async (save) => {
    await save({
      tags: editTags,
      groupes: editGroupes,
    });
  };

  return (
    <EditableBlock
      title="Segmentation"
      icon="bi-tags"
      testid="contact-segmentation-block"
      onSave={onSave}
      renderView={() => (
        <div className="space-y-4">
          <p className="text-[11px] text-slate-500 -mt-2">
            <i className="bi bi-info-circle"></i> <strong>Tags</strong> = étiquettes libres. <strong>Groupes</strong>{' '}
            = segments officiels (campagnes marketing).
          </p>

          {/* Origine */}
          {contact?.origine && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                Origine <span className="text-slate-400 normal-case ml-1">(non modifiable)</span>
              </p>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-md text-xs font-semibold text-slate-700">
                <i className="bi bi-magic"></i>
                {contact.origine}
              </span>
            </div>
          )}

          {/* Groupes officiels */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Groupes officiels
            </p>
            {(contact?.groupes || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucun groupe</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {contact.groupes.map((g, i) => (
                  <span
                    key={`g-${i}`}
                    data-testid={`contact-groupe-${i}`}
                    className="px-2 py-0.5 bg-slate-900 text-white rounded-md text-[11px] font-semibold inline-flex items-center gap-1"
                  >
                    <i className="bi bi-star-fill text-yellow-400 text-[10px]"></i>
                    {g}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Tags personnels */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Tags personnels
            </p>
            {(contact?.tags || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucun tag</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {contact.tags.map((t, i) => (
                  <span
                    key={`t-${i}`}
                    data-testid={`contact-tag-${i}`}
                    className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md text-[11px] font-semibold"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      renderEdit={({ closeEdit, save, saving }) => (
        <div className="space-y-5">
          <p className="text-[11px] text-slate-500">
            <i className="bi bi-info-circle"></i> <strong>Tags</strong> = étiquettes libres (suivi perso).{' '}
            <strong>Groupes</strong> = segments officiels (campagnes marketing).
          </p>

          {/* Origine read-only */}
          {contact?.origine && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                Origine <span className="text-slate-400 normal-case ml-1">(non modifiable)</span>
              </p>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-md text-xs font-semibold text-slate-700">
                <i className="bi bi-magic"></i>
                {contact.origine}
              </span>
            </div>
          )}

          {/* Groupes - Edit */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Groupes officiels
            </p>
            <div className="flex flex-wrap gap-1.5 items-center">
              {editGroupes.map((g, i) => (
                <span
                  key={`g-edit-${i}`}
                  className="px-2 py-0.5 bg-slate-900 text-white rounded-md text-[11px] font-semibold inline-flex items-center gap-1"
                >
                  <i className="bi bi-star-fill text-yellow-400 text-[10px]"></i>
                  {g}
                  <button
                    type="button"
                    onClick={() => removeGroupe(g)}
                    data-testid={`remove-groupe-${g}`}
                    className="hover:text-rose-300 ml-0.5"
                  >
                    <i className="bi bi-x text-sm"></i>
                  </button>
                </span>
              ))}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowGroupePicker((v) => !v)}
                  data-testid="add-groupe-btn"
                  className="px-2 py-0.5 border border-dashed border-slate-400 hover:border-slate-700 text-slate-600 hover:text-slate-900 rounded-md text-[11px] font-semibold"
                >
                  <i className="bi bi-plus"></i> Sélectionner un groupe
                </button>
                {showGroupePicker && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowGroupePicker(false)}
                    ></div>
                    <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 max-h-64 overflow-auto">
                      {availableGroupes.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-400">Aucun groupe disponible</p>
                      ) : (
                        availableGroupes
                          .filter((g) => !editGroupes.includes(g))
                          .map((g, i) => (
                            <button
                              key={`gp-${i}`}
                              type="button"
                              onClick={() => addGroupe(g)}
                              className="w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 text-slate-700"
                            >
                              {g}
                            </button>
                          ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Tags - Edit */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Tags personnels
            </p>
            <div className="flex flex-wrap gap-1.5 items-center mb-2">
              {editTags.map((t, i) => (
                <span
                  key={`t-edit-${i}`}
                  className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md text-[11px] font-semibold inline-flex items-center gap-1"
                >
                  #{t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    data-testid={`remove-tag-${t}`}
                    className="text-amber-600 hover:text-rose-600"
                  >
                    <i className="bi bi-x text-sm"></i>
                  </button>
                  <button
                    type="button"
                    title="Promouvoir en groupe officiel"
                    onClick={() => promoteTagToGroup(t)}
                    data-testid={`promote-tag-${t}`}
                    className="text-amber-700 hover:text-slate-900 ml-0.5"
                  >
                    <i className="bi bi-arrow-up-circle text-xs"></i>
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Nouveau tag…"
                data-testid="new-tag-input"
                className="flex-1 max-w-xs px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 outline-none"
              />
              <button
                type="button"
                onClick={handleAddTag}
                data-testid="add-tag-btn"
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold"
              >
                + Ajouter tag
              </button>
            </div>
          </div>

          <EditableBlockFooter
            onCancel={closeEdit}
            onSave={() => handleSave(save)}
            saving={saving}
            testid="segmentation-block"
          />
        </div>
      )}
    />
  );
};

export default SegmentationBlock;
